<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': hasSubmittedMapping,
      'strengths-mapping': questionnaireType === 'strengths',
      'indicators-mapping': questionnaireType === 'indicators',
    }"
    :data-test="`session-slide-${index}`"
  >
    <template #text>
      <transition name="submitted">
        <StrengthsMappingWrapper
          v-if="!cmsSessionStore.hasFinishedMapping[questionnaireType]"
          id="strengths-mapping"
          :can-re-map-strengths="props.canReMapStrengths"
          :questionnaire-type="questionnaireType"
          :data-test="
            questionnaireType === 'strengths'
              ? 'strength-questionnaire-slide'
              : 'indicator-questionnaire-slide'
          "
        />
      </transition>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from "vue";
import { useToggle } from "../../../composables/useToggle";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import SessionSlide from "../../sessions/session-slide.vue";
import StrengthsMappingWrapper from "../../sessions/strengths-mapping-wrapper.vue";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import { CmsStrengthMapping, QuestionnaireType } from "../../../services/cms/cms-type-definitions";

const props = defineProps<{
  slice: PrismicSliceWithData;
  index: number;
  canReMapStrengths: boolean;
}>();
const emit = defineEmits(["onCollapse", "onIndicatorCollapse"]);

const prismicSlice = computed<CmsStrengthMapping>(() => {
  return props.slice.prismicSlice as CmsStrengthMapping;
});

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const hasSubmittedMapping = ref(false);

const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

const questionnaireType = computed(
  () => prismicSlice.value.primary?.questionnaire_type ?? "strengths",
);

function emitCollapseEvent(type: QuestionnaireType): void {
  if (type === "strengths" && !hasSubmittedMapping.value) {
    emit("onCollapse", props.index);
  }
  if (type === "indicators") {
    emit("onIndicatorCollapse", props.index);
  }
}

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(props.slice.section)) {
    toggleIsDisplayed();
  }
});

watch(
  () => cmsSessionStore.hasFinishedMapping,
  (newVal) => {
    if (newVal[questionnaireType.value]) {
      emitCollapseEvent(questionnaireType.value);
      hasSubmittedMapping.value = true;
      cmsSessionStore.updateSectionStatus(props.slice.section, true);
    }
  },
  { deep: true, immediate: true },
);

defineExpose({
  slide,
  visualDiv,
});
</script>
